import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type EmployeeService from 'tio-employee/services/employee';
import formatDateString from 'tio-common/helpers/format-date-string';
import Svg from 'tio-employee/components/svg';
import { t } from 'ember-intl';
import Tile from '../tile';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import type { IntlService } from 'ember-intl';
import Button from 'tio-ui/components/button';
import { on } from '@ember/modifier';
import { PhWarning } from 'ember-phosphor-icons';
import Text from 'tio-ui/components/text';

class DashboardWidgetSecureYourFutureComponent extends Component {
  @service declare employee: EmployeeService;
  @service declare intl: IntlService;

  get isEnrollmentComplete() {
    return this.employee?.current?.secureEnrollmentComplete;
  }

  get isEnrollmentInProgress() {
    return this.employee?.current?.secureEnrollmentInProgress;
  }

  get benefitStartDate() {
    return this.employee?.current?.secureBenefitStartDate;
  }

  get isEligibleToEnroll() {
    if (!this.benefitStartDate) {
      return false;
    }
    // Compare dates only (ignore time) to avoid timezone issues
    const startDate = new Date(this.benefitStartDate);
    const today = new Date();

    // Create new Date objects with time stripped for comparison (avoid mutation)
    const startDateMidnight = new Date(startDate.setHours(0, 0, 0, 0));
    const todayMidnight = new Date(today.setHours(0, 0, 0, 0));

    return startDateMidnight <= todayMidnight;
  }

  get incompleteMessageKey() {
    // Different message if they started vs never began
    return this.isEnrollmentInProgress
      ? 'dashboard_tiles.secure_your_future_in_progress'
      : 'dashboard_tiles.secure_your_future_not_started';
  }

  get enrollButtonTextKey() {
    return this.isEnrollmentInProgress
      ? 'dashboard_tiles.continue_enrollment'
      : 'dashboard_tiles.start_enrollment';
  }

  <template>
    {{#if this.isEnrollmentComplete}}
      {{! Enrollment Complete - show original completed view }}
      <LinkTo @route="authenticated.syf.dashboard">
        <Tile @headerText={{t "syf.default"}}>
          <:description>
            <Text @tag="p">
              <span class="font-semibold">{{t "syf.match_401k"}}</span>
              -
              <span class="font-semibold">{{t "syf.enrollment.complete"}}</span>
            </Text>
            <Text @tag="p">{{t "dashboard_tiles.secure_your_future_complete"}}</Text>
            <Text @tag="p" class="mt-2">
              {{t "dashboard_tiles.benefit_available_on"}}
              <span class="font-semibold">{{formatDateString this.benefitStartDate}}</span>
            </Text>
          </:description>
          <:image>
            <Svg
              @name="secure-your-future"
              width="100%"
              height="100%"
              role="img"
              aria-label={{t "svg.secure_your_future"}}
            />
          </:image>
        </Tile>
      </LinkTo>
    {{else}}
      {{! Not Complete (NOT_STARTED or DRAFT) - show alert with button }}
      <LinkTo @route="authenticated.syf.enrollment">
        <Tile @headerText={{t "syf.default"}}>
          <:body>
            <div class="tio-col-flush h-full">
              <Text @tag="p" class="mb-2">
                <span class="font-semibold">{{t "syf.match_401k"}}</span>
                -
                <span class="font-semibold">{{t "syf.enrollment.incomplete"}}</span>
              </Text>
              <Text @variant="error" @size="sm" class="tio-row-top-tight">
                <PhWarning @size={{20}} class="shrink-0 mt-0.5" />
                <span>{{t this.incompleteMessageKey}}</span>
              </Text>
              <Text @tag="p" @size="sm" class="mt-2">
                {{t "dashboard_tiles.benefit_available_on"}}
                <span class="font-semibold">{{formatDateString this.benefitStartDate}}</span>
              </Text>
              {{#if this.isEligibleToEnroll}}
                <div class="tio-flex-center mt-auto pt-2 pb-4">
                  <Button
                    @intent="primary"
                    class="uppercase"
                    {{on "click" (transitionTo "authenticated.syf.enrollment")}}
                  >
                    {{t this.enrollButtonTextKey}}
                  </Button>
                </div>
              {{/if}}
            </div>
          </:body>
        </Tile>
      </LinkTo>
    {{/if}}
  </template>
}

export default DashboardWidgetSecureYourFutureComponent;
